import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { ArrowRight, CircleNotch, Plus, Clock, CaretRight, CaretDown, PushPin } from "@phosphor-icons/react";
import { TOOLS, PILLARS, PILLAR_ORDER, statusStyle, statusLabel, openPath, editorPath, fmtDate } from "@/lib/tools";
import { useAuth } from "@/context/AuthContext";
import TzuConcierge from "@/components/TzuConcierge";

export default function Dashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [openPillars, setOpenPillars] = useState({});
  const [pinning, setPinning] = useState(null);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const pinnedSet = new Set(user?.pinned_tools || []);

  const togglePin = async (e, toolType) => {
    e.stopPropagation();
    setPinning(toolType);
    try {
      const res = await api.put("/user/pinned", { tool_type: toolType });
      setUser((u) => ({ ...u, pinned_tools: res.data.pinned_tools }));
    } catch {
      toast.error("Could not update pin");
    } finally {
      setPinning(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/documents");
        setItems(res.data);
      } catch {
        toast.error("Failed to load workspace");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const createDoc = async (toolType) => {
    setCreating(toolType);
    try {
      const res = await api.post("/documents", { tool_type: toolType });
      navigate(editorPath(res.data));
    } catch {
      toast.error("Could not create document");
      setCreating(null);
    }
  };

  // group tools by pillar, hoisting pinned tools to the front (stable sort preserves order)
  const toolsByPillar = PILLAR_ORDER.reduce((acc, p) => {
    const list = Object.values(TOOLS).filter((t) => t.pillar === p);
    list.sort((a, b) => (pinnedSet.has(b.type) ? 1 : 0) - (pinnedSet.has(a.type) ? 1 : 0));
    acc[p] = list;
    return acc;
  }, {});

  const recent = items.slice(0, 5);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto w-full py-10 px-6 lg:px-10">
        <p className="text-xs font-semibold tracking-[0.12em] uppercase text-slate-500">Workspace</p>
        <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Start a new document</h1>
        <p className="mt-2 text-slate-500 max-w-2xl">
          Pick a tool to begin. Each tool is organised by leadership pillar — Strategy, Operations and People.
        </p>

        <div className="mt-6"><TzuConcierge /></div>

        {/* Pillar launchpad — collapsible */}
        <div className="mt-8 space-y-4" data-testid="launchpad">
          {PILLAR_ORDER.map((pkey) => {
            const pillar = PILLARS[pkey];
            const tools = toolsByPillar[pkey];
            if (!tools.length) return null;
            const isOpen = !!openPillars[pkey];
            return (
              <section key={pkey} data-testid={`pillar-${pkey.toLowerCase()}`} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <button
                  data-testid={`pillar-toggle-${pkey.toLowerCase()}`}
                  onClick={() => setOpenPillars((p) => ({ ...p, [pkey]: !p[pkey] }))}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${pillar.dot}`} />
                    <h2 className={`font-mono text-sm font-bold uppercase tracking-[0.1em] ${pillar.text}`}>{pillar.label}</h2>
                    <span className="text-sm text-slate-400 hidden md:inline truncate">— {pillar.blurb}</span>
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold shrink-0 ${pillar.chip}`}>{tools.length} tools</span>
                  </div>
                  <CaretDown size={18} className={`text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 pt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 border-t border-slate-100">
                    {tools.map((t) => {
                      const isPinned = pinnedSet.has(t.type);
                      return (
                      <button
                        key={t.type}
                        data-testid={`tool-tile-${t.type}`}
                        onClick={() => createDoc(t.type)}
                        disabled={creating === t.type}
                        className={`group relative text-left bg-white border rounded-sm p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${pillar.ring} disabled:opacity-60 ${isPinned ? "border-slate-400 ring-1 ring-slate-200" : "border-slate-200"}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className={`w-11 h-11 rounded-sm flex items-center justify-center ${pillar.chip}`}>
                            <t.icon size={22} weight="duotone" />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wide ${pillar.chip}`}>
                              {pillar.label}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              data-testid={`tool-pin-${t.type}`}
                              aria-pressed={isPinned}
                              aria-label={isPinned ? "Unpin tool" : "Pin tool"}
                              onClick={(e) => togglePin(e, t.type)}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") togglePin(e, t.type); }}
                              className={`p-1 rounded-sm transition-colors ${isPinned ? "text-slate-700" : "text-slate-300 hover:text-slate-600"}`}
                            >
                              {pinning === t.type ? <CircleNotch size={16} className="animate-spin" /> : <PushPin size={16} weight={isPinned ? "fill" : "regular"} />}
                            </span>
                          </div>
                        </div>
                        <h3 className="mt-4 text-lg font-semibold text-slate-900">{t.name}</h3>
                        <p className="mt-1 text-sm text-slate-500 leading-relaxed">{t.desc}</p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 group-hover:gap-2.5 transition-all">
                          {creating === t.type ? (
                            <><CircleNotch size={16} className="animate-spin" /> Creating…</>
                          ) : (
                            <><Plus size={16} weight="bold" /> New document <ArrowRight size={14} /></>
                          )}
                        </span>
                      </button>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        {/* Recent work */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock size={18} weight="duotone" className="text-slate-500" />
              <h2 className="font-mono text-sm font-bold uppercase tracking-[0.1em] text-slate-700">Recent Work</h2>
            </div>
            <button
              data-testid="view-all-library"
              onClick={() => navigate("/library")}
              className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
            >
              View all in Library <CaretRight size={14} />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400 bg-white border border-slate-200 rounded-sm">
              <CircleNotch size={26} className="animate-spin" />
            </div>
          ) : recent.length === 0 ? (
            <div className="py-14 text-center border border-dashed border-slate-300 rounded-sm bg-white text-slate-500">
              No documents yet. Create one from a tool above to get started.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="recent-work-table">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3 font-semibold">Title</th>
                    <th className="px-5 py-3 font-semibold hidden md:table-cell">Tool</th>
                    <th className="px-5 py-3 font-semibold hidden lg:table-cell">Pillar</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold hidden sm:table-cell">Updated</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recent.map((d) => {
                    const pillar = PILLARS[d.pillar] || PILLARS.OPERATIONS;
                    return (
                      <tr
                        key={d.id}
                        data-testid={`recent-row-${d.id}`}
                        onClick={() => navigate(openPath(d))}
                        className="hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-5 py-3.5 font-medium text-slate-900 max-w-xs truncate">{d.title}</td>
                        <td className="px-5 py-3.5 text-sm text-slate-600 hidden md:table-cell">{d.tool_name}</td>
                        <td className="px-5 py-3.5 hidden lg:table-cell">
                          <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wide ${pillar.chip}`}>{pillar.label}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${statusStyle[d.status]}`}>{statusLabel(d.status)}</span>
                        </td>
                        <td className="px-5 py-3.5 font-mono text-xs text-slate-400 hidden sm:table-cell">{fmtDate(d.updated_at || d.created_at)}</td>
                        <td className="px-5 py-3.5 text-right"><CaretRight size={16} className="text-slate-300 inline" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
